import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { unitId, actionId } from '@/annotations'
import { WizardProse } from '@/components/WizardParts'
import { Button } from '@/components/Button'
import { Note } from '@/components/Note'
import { Chip } from '@/components/Chip'
import { Alert } from '@/components/Alert'
import { getApiClient, ApiProblemError, type Problem } from '@/data/apiClient'
import { OB_WZ_4_COPY } from '@/copy/onboarding'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { MOptgap } from '@/screens/m-optgap/MOptgap'
import type { components } from '@/api/generated/schema'

type ScopeCheckRun = components['schemas']['ScopeCheckRun']
type ScopeProbeState = components['schemas']['ScopeProbeState']

const ROW_ICON: Record<ScopeProbeState, string> = {
  PENDING: '○',
  CHECKING: '⟳',
  PASS: '✓',
  FAIL_REQUIRED: '✗',
  FAIL_OPTIONAL: '⚠',
  INDETERMINATE: '?',
  NOT_APPLICABLE: '–',
}

const ROW_COLOR: Record<ScopeProbeState, string> = {
  PENDING: 'text-muted',
  CHECKING: 'text-blue',
  PASS: 'text-ok',
  FAIL_REQUIRED: 'text-fail',
  FAIL_OPTIONAL: 'text-warn',
  INDETERMINATE: 'text-muted',
  NOT_APPLICABLE: 'text-muted',
}

/**
 * `ob-wz-4` — the scope check (design/12-onboarding-and-credentials.md §12.4.5, `LLD-API-091`).
 * `scopeCheckStart` fires on entry; the run is then polled via `scopeCheckGet` every 750ms
 * (TanStack Query's `refetchInterval`, which is exactly this pattern) until it leaves `RUNNING`.
 * `can_continue` is a served field (LLD-SPA-172) — nothing here computes it.
 *
 * **`pane`/`grid-tools`/`grid-footer`/`wizard-buttons` added W166** — each wraps content this
 * screen already had (the root, the "Recheck all" control, the closing footer note, the bottom
 * button row); no new content.
 */
export function ObWz4({
  orgId,
  workspaceId,
  workspaceName,
  onPassed,
  onBack,
}: {
  orgId: string
  workspaceId: string
  workspaceName: string
  onPassed: () => void
  onBack: () => void
}) {
  const queryClient = useQueryClient()
  const [gapModalOpen, setGapModalOpen] = useState(false)

  const start = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.POST('/orgs/{org_id}/workspaces/{workspace_id}/scope-checks', {
        params: { path: { org_id: orgId, workspace_id: workspaceId } },
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data
    },
    onSuccess: (run) => {
      if (run) queryClient.setQueryData(['scope-check', run.run_id], run)
    },
  })

  useEffect(() => {
    start.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const runId = start.data?.run_id

  const poll = useQuery<ScopeCheckRun>({
    queryKey: ['scope-check', runId],
    enabled: Boolean(runId),
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/scope-checks/{run_id}', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, run_id: runId! } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data as ScopeCheckRun
    },
    refetchInterval: (query) => (query.state.data?.state === 'RUNNING' ? 750 : false),
  })

  const startProblem = start.error instanceof ApiProblemError ? start.error.problem : undefined
  const run = (poll.data ?? start.data) as ScopeCheckRun | undefined

  return (
    <div data-scr={unitId('ob-wz-4')} data-cmp="pane" className="p-10">
      <h3 className="m-0 mb-3 text-heading font-semibold">{OB_WZ_4_COPY.heading}</h3>
      <WizardProse>{OB_WZ_4_COPY.proseTemplate(workspaceName)}</WizardProse>

      {startProblem && (isUnexpectedRefusal(startProblem.refusal_kind) ? <UnexpectedRefusal problem={startProblem} /> : <Note>{startProblem.title}</Note>)}

      {run && (
        <div data-cmp="panel" className="bg-panel border border-bd rounded-2">
          <div data-cmp="panel-header" className="flex items-center gap-5 p-6 border-b border-bd">
            <h3 className="m-0 text-body-lg font-semibold flex-1">{OB_WZ_4_COPY.panelHeading}</h3>
            <Chip variant="plain">
              {run.state === 'RUNNING' ? OB_WZ_4_COPY.notRunLabel + '…' : run.state}
            </Chip>
            <div data-cmp="grid-tools">
              <Button data-act={actionId('scopeCheck.start')} ghost disabled={run.state === 'RUNNING' || start.isPending} onClick={() => start.mutate()}>
                Recheck all
              </Button>
            </div>
          </div>

          {run.state === 'WRONG_TOKEN_KIND' ? (
            <div className="p-6">
              <Alert variant="danger">{run.terminal_message ?? OB_WZ_4_COPY.wrongTokenKindNote}</Alert>
            </div>
          ) : (
            <div>
              {run.rows.map((row) => (
                <div key={row.scope} className="flex items-start gap-5 p-6 border-b border-bd last:border-b-0">
                  <span className={`w-5 text-center ${ROW_COLOR[row.state]}`} aria-hidden="true">
                    {ROW_ICON[row.state]}
                  </span>
                  <div>
                    <div>
                      <b>{row.label}</b>{' '}
                      <span className="text-small text-muted">{row.required ? OB_WZ_4_COPY.requiredLabel : OB_WZ_4_COPY.optionalLabel}</span>
                    </div>
                    <div className="text-small text-muted font-mono">{row.scope}</div>
                    {row.detail && <Note>{row.detail}</Note>}
                  </div>
                </div>
              ))}
              <div data-cmp="grid-footer" className="border-t border-bd p-6">
                <Note>{OB_WZ_4_COPY.footNote}</Note>
              </div>
            </div>
          )}
        </div>
      )}

      {run && run.optional_gaps && run.optional_gaps.length > 0 && run.can_continue && (
        <div className="mt-7">
          <Note>{OB_WZ_4_COPY.acknowledgeGapsPrompt}</Note>
        </div>
      )}

      <div data-cmp="wizard-buttons" className="flex gap-5 justify-center mt-9">
        <Button data-act={actionId('nav.ob-wz-3')} ghost onClick={onBack}>
          {OB_WZ_4_COPY.backButton}
        </Button>
        <Button
          data-act={run?.optional_gaps && run.optional_gaps.length > 0 ? actionId('scopeCheck.acknowledgeGaps') : actionId('nav.ob-wz-6')}
          disabled={!run?.can_continue}
          onClick={() => {
            if (!run) return
            if (run.optional_gaps && run.optional_gaps.length > 0) {
              setGapModalOpen(true)
            } else {
              onPassed()
            }
          }}
        >
          {run?.optional_gaps && run.optional_gaps.length > 0 ? OB_WZ_4_COPY.acknowledgeButton : OB_WZ_4_COPY.continueButton}
        </Button>
      </div>

      {gapModalOpen && run?.optional_gaps && run.optional_gaps.length > 0 && (
        <MOptgap
          orgId={orgId}
          workspaceId={workspaceId}
          runId={run.run_id}
          workspaceName={workspaceName}
          gaps={run.optional_gaps.map((scope) => ({
            scope,
            label: run.rows.find((row) => row.scope === scope)?.label ?? scope,
          }))}
          onDismiss={() => setGapModalOpen(false)}
          onAcknowledged={onPassed}
        />
      )}
    </div>
  )
}
